import { useConfigStore } from '@/store/config.store';
import { applyPanTransformation } from '@/commons/fabricjs/FabricPanCallback';
import { CANVAS_ZOOM_MAX } from '@/config';
import { Point, type Canvas, type TPointerEvent, type TPointerEventInfo } from 'fabric';
import { storeToRefs } from 'pinia';
import type { ITouchZoomState } from '@/typescript/interfaces/ITouchZoomState';
import type { Ref } from 'vue';

//#region Initialization & Setup

/**
 * returned a callback function initialized with a given canvas context
 * @param fabricCanvas instance of fabric.Canvas
 * @param zoomFactorMax zomm limit
 * @returns function to be used as callback for zoom event
 */
function initZoomCallback(
    fabricCanvas: Canvas | null,
    minZoom: number = 0.75,
    maxZoom: number = CANVAS_ZOOM_MAX,
): ((opt: TPointerEventInfo) => void) | Error {
    const { fZoom } = storeToRefs(useConfigStore());
    if (!fabricCanvas) {
        return new Error('FabricCanvas need to be initialized before initZoomCallback function');
    }
    // returned callback with closure context
    return (opt: TPointerEventInfo) => {
        const { e: wheelEvent }: { e: TPointerEvent } = opt;
        if (!(wheelEvent instanceof WheelEvent)) {
            return;
        }
        wheelEvent.preventDefault();
        wheelEvent.stopPropagation();
        const { clientX, clientY, deltaY } = wheelEvent;
        const zoom = fabricCanvas.getZoom();
        const zoomFactor = zoom - deltaY / 3000;
        const zoomCenter = new Point(clientX, clientY);
        if (minZoom <= zoomFactor && zoomFactor <= maxZoom) {
            fabricCanvas.zoomToPoint(zoomCenter, zoomFactor);
            fZoom.value = fabricCanvas.getZoom();
        }
        fabricCanvas?.renderAll();
    };
}

/**
 * Pinch-to-zoom callback for mobile touch devices
 * @param fabricCanvas instance of fabric.Canvas
 * @param minZoom minimum zoom level
 * @param maxZoom maximum zoom level
 * @returns function to be used as callback for touch events
 */
function initTouchZoomCallback(
    fabricCanvas: Canvas | null,
    minZoom: number = 0.75,
    maxZoom: number = 1.5,
): ((opt: TPointerEventInfo) => void) | Error {
    const { fZoom } = storeToRefs(useConfigStore());
    if (!fabricCanvas) {
        return new Error('FabricCanvas need to be initialized before initTouchZoomCallback function');
    }

    let lastDistance = 0;
    let lastZoom = 1;
    let isZooming = false;

    return (opt: TPointerEventInfo) => {
        const { e }: { e: TPointerEvent } = opt;

        // Only handle touch events
        if (!(e instanceof TouchEvent)) {
            return;
        }

        if (e.touches.length === 2) {
            e.preventDefault();
            e.stopPropagation();

            isZooming = true;

            const touch1 = e.touches[0];
            const touch2 = e.touches[1];

            // Calculate distance between two fingers
            const distance = Math.sqrt(Math.pow(touch2.clientX - touch1.clientX, 2) + Math.pow(touch2.clientY - touch1.clientY, 2));

            // Calculate center point between fingers relative to canvas
            const canvasRect = fabricCanvas.getElement().getBoundingClientRect();
            const centerX = (touch1.clientX + touch2.clientX) / 2 - canvasRect.left;
            const centerY = (touch1.clientY + touch2.clientY) / 2 - canvasRect.top;
            const zoomCenter = new Point(centerX, centerY);

            if (e.type === 'touchstart' || lastDistance === 0) {
                // Initialize on first pinch
                lastDistance = distance;
                lastZoom = fabricCanvas.getZoom();
                return;
            }

            if (e.type === 'touchmove' && lastDistance > 0) {
                // Calculate zoom factor based on distance change
                const scaleFactor = distance / lastDistance;
                const newZoom = lastZoom * scaleFactor;

                // Apply zoom constraints
                if (minZoom <= newZoom && newZoom <= maxZoom) {
                    fabricCanvas.zoomToPoint(zoomCenter, newZoom);
                    fZoom.value = fabricCanvas.getZoom();
                    fabricCanvas.renderAll();
                }

                lastDistance = distance;
                lastZoom = fabricCanvas.getZoom();
            }
        } else if (e.type === 'touchend' && isZooming) {
            // Reset when pinch gesture ends
            lastDistance = 0;
            isZooming = false;
        }
    };
}

/**
 * Creates and initializes touch zoom state
 */
function createTouchZoomState(): ITouchZoomState {
    return {
        lastDistance: 0,
        lastZoom: 1,
        lastTouchCenter: new Point(0, 0),
        isZooming: false,
        zoomTimeout: null,
    };
}

//#endregion

//#region Event Handlers

/**
 * Handles two-finger touch gestures (zoom + pan)
 */
function handleTwoFingerTouch(
    e: TouchEvent,
    state: ITouchZoomState,
    fabricCanvas: Canvas,
    minZoom: number,
    maxZoom: number,
    fZoom: Ref<number>,
): boolean {
    if (e.touches.length !== 2) return false;

    e.preventDefault();
    e.stopPropagation();

    const touch1 = e.touches[0];
    const touch2 = e.touches[1];
    const distance = calculateTouchDistance(touch1, touch2);
    const canvasRect = fabricCanvas.getElement().getBoundingClientRect();
    const touchCenter = calculateTouchCenter(touch1, touch2, canvasRect);

    if (e.type === 'touchstart' || state.lastDistance === 0) {
        initializePinchZoom(state, distance, touchCenter, fabricCanvas);
        return true;
    }

    if (e.type === 'touchmove' && state.lastDistance > 0 && state.isZooming) {
        setZoomResetTimeout(state);

        // Handle zoom
        const zoomScaleFactor = distance / state.lastDistance;
        const newZoom = state.lastZoom * zoomScaleFactor;
        applyConstrainedZoom(fabricCanvas, touchCenter, newZoom, minZoom, maxZoom, fZoom);

        // Handle pan (movement of the center point between fingers)
        const panDelta = new Point(touchCenter.x - state.lastTouchCenter.x, touchCenter.y - state.lastTouchCenter.y);
        applyPanTransformation(fabricCanvas, panDelta);

        // Update state for next iteration
        updateTouchZoomState(state, distance, touchCenter, fabricCanvas);
        fabricCanvas.renderAll();
    }

    return true;
}

/**
 * Handles touch end/cancel events for zoom gestures
 */
function handleTouchEnd(e: TouchEvent, state: ITouchZoomState): boolean {
    if (e.type === 'touchend' || e.type === 'touchcancel') {
        // Reset when any touch gesture ends or is cancelled
        // Also reset if we don't have 2 touches anymore
        if (e.touches.length < 2) {
            resetTouchZoomState(state);
        }
        return true;
    }
    return false;
}

/**
 * Validates and resets zoom state if conditions are invalid
 */
function validateZoomState(e: TPointerEvent, state: ITouchZoomState): void {
    // Safety check: if we're marked as zooming but don't have a TouchEvent with 2 fingers, reset
    if (window.TouchEvent && state.isZooming && (!(e instanceof window.TouchEvent) || e.touches.length !== 2)) {
        resetTouchZoomState(state);
    }

    // If we were zooming but now have different number of touches, reset
    if (window.TouchEvent && e instanceof window.TouchEvent && e.touches.length !== 2 && state.isZooming) {
        resetTouchZoomState(state);
    }
}

//#endregion

//#region Zoom Operations

/**
 * Handles the initialization of a pinch zoom gesture
 */
function initializePinchZoom(state: ITouchZoomState, distance: number, touchCenter: Point, fabricCanvas: Canvas): void {
    state.lastDistance = distance;
    state.lastZoom = fabricCanvas.getZoom();
    state.lastTouchCenter = touchCenter;
    state.isZooming = true;
    setZoomResetTimeout(state);
}

/**
 * Applies zoom to the canvas with constraints
 */
function applyConstrainedZoom(
    fabricCanvas: Canvas,
    touchCenter: Point,
    newZoom: number,
    minZoom: number,
    maxZoom: number,
    fZoom: Ref<number>,
): void {
    if (minZoom <= newZoom && newZoom <= maxZoom) {
        fabricCanvas.zoomToPoint(touchCenter, newZoom);
        fZoom.value = fabricCanvas.getZoom();
    }
}

//#endregion

//#region State Management

/**
 * Resets the touch zoom state to initial values
 */
function resetTouchZoomState(state: ITouchZoomState): void {
    state.lastDistance = 0;
    state.isZooming = false;
    if (state.zoomTimeout) {
        clearTimeout(state.zoomTimeout);
        state.zoomTimeout = null;
    }
}

/**
 * Updates the touch zoom state after a successful zoom/pan operation
 */
function updateTouchZoomState(state: ITouchZoomState, distance: number, touchCenter: Point, fabricCanvas: Canvas): void {
    state.lastDistance = distance;
    state.lastZoom = fabricCanvas.getZoom();
    state.lastTouchCenter = touchCenter;
}

/**
 * Sets a timeout to reset zoom state after inactivity
 */
function setZoomResetTimeout(state: ITouchZoomState): void {
    if (state.zoomTimeout) {
        clearTimeout(state.zoomTimeout);
    }
    // Reset zoom state after 500ms of inactivity
    state.zoomTimeout = setTimeout(() => resetTouchZoomState(state), 500);
}

//#endregion

//#region Math Utilities

/**
 * Calculates the distance between two touch points
 */
function calculateTouchDistance(touch1: Touch, touch2: Touch): number {
    return Math.sqrt(Math.pow(touch2.clientX - touch1.clientX, 2) + Math.pow(touch2.clientY - touch1.clientY, 2));
}

/**
 * Calculates the center point between two touches relative to canvas
 */
function calculateTouchCenter(touch1: Touch, touch2: Touch, canvasRect: DOMRect): Point {
    const centerX = (touch1.clientX + touch2.clientX) / 2 - canvasRect.left;
    const centerY = (touch1.clientY + touch2.clientY) / 2 - canvasRect.top;
    return new Point(centerX, centerY);
}

//#endregion

export { createTouchZoomState, handleTouchEnd, handleTwoFingerTouch, initTouchZoomCallback, initZoomCallback, validateZoomState };
